// src/pages/BrowseRoles.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Form, Spinner, Row, Col, Modal } from 'react-bootstrap';
import { Search, X, FilterX, Filter } from 'lucide-react';
import { fetchPublicRoleListApi } from '../services/api';
import ModernAuditionCard from '../components/ModernAuditionCard';
import '../components/css/BrowseRoles.css'; 

const BrowseRoles = () => {
  const [allRoles, setAllRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Filter States
  const [selectedAuditionType, setSelectedAuditionType] = useState('All Types');
  const [selectedCity, setSelectedCity] = useState('All Cities');
  const [selectedTalentType, setSelectedTalentType] = useState('All Talent');

  // Mobile Popup State
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const loadAllRoles = async () => {
      try {
        setLoading(true);
        const { data } = await fetchPublicRoleListApi();
        setAllRoles(data?.data || []);
      } catch (err) {
        console.error('Error loading roles:', err);
        setAllRoles([]);
      } finally { setLoading(false); }
    };
    loadAllRoles();
  }, []);

  // ... (all useMemo hooks remain the same) ...
  const auditionTypes = useMemo(() => {
    const set = new Set();
    allRoles.forEach((item) => {
      const type = item.auditionType || item.audition_type || '';
      if (type && String(type).trim()) set.add(String(type).trim());
    });
    return ['All Types', ...Array.from(set)];
  }, [allRoles]);

  const cities = useMemo(() => {
    const set = new Set();
    allRoles.forEach((item) => {
      const city = item.city || item.projectCity || item.location || item.roleCity || item['project.city'] || '';
      if (city && String(city).trim()) set.add(String(city).trim());
    });
    return ['All Cities', ...Array.from(set)];
  }, [allRoles]);

  const talentTypes = useMemo(() => {
    const set = new Set();
    allRoles.forEach((item) => {
      const type = item.roleCategory || item.role_category || item.category || item.talentType || '';
      if (type && String(type).trim()) set.add(String(type).trim());
    });
    return ['All Talent', ...Array.from(set)];
  }, [allRoles]);

  const filteredRoles = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return allRoles.filter((item) => {
      const roleName = (item.roleName || item.role_name || item.roleTitle || '').toLowerCase();
      const projectName = (item.projectName || item.project_name || item['project.projectName'] || '').toLowerCase();
      const city = (item.city || item.projectCity || item.location || item.roleCity || item['project.city'] || '').toLowerCase();
      const talentType = (item.roleCategory || item.role_category || item.category || item.talentType || '').toLowerCase();
      const auditionType = (item.auditionType || item.audition_type || '').toLowerCase();

      const matchesSearch = !term || roleName.includes(term) || projectName.includes(term);
      const matchesAuditionType = selectedAuditionType === 'All Types' || auditionType === selectedAuditionType.toLowerCase() || auditionType.includes(selectedAuditionType.toLowerCase());
      const matchesCity = selectedCity === 'All Cities' || city === selectedCity.toLowerCase() || city.includes(selectedCity.toLowerCase());
      const matchesTalentType = selectedTalentType === 'All Talent' || talentType === selectedTalentType.toLowerCase() || talentType.includes(selectedTalentType.toLowerCase());

      return matchesSearch && matchesAuditionType && matchesCity && matchesTalentType;
    });
  }, [allRoles, searchTerm, selectedAuditionType, selectedCity, selectedTalentType]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedAuditionType('All Types');
    setSelectedCity('All Cities');
    setSelectedTalentType('All Talent');
  };

  const hasActiveFilters = selectedAuditionType !== 'All Types' || selectedCity !== 'All Cities' || selectedTalentType !== 'All Talent';

  return (
    <div className="browse-roles-page">
      <section className="browse-roles-hero">
        <Container fluid className="px-3 px-md-4 px-xl-5">
          
          <div className="browse-roles-header-wrapper">
            <div className="browse-roles-title-wrap">
              <h1 className="browse-roles-title">Browse <span>Roles</span></h1>
              <p className="browse-roles-subtitle">Find your next big opportunity from our verified listings.</p>
            </div>
          </div>

          <div className="top-filter-bar">
            {/* ... (filter bar logic remains the same) ... */}
            <button className="mobile-filter-icon-btn d-lg-none" onClick={() => setShowMobileFilters(true)}>
              <Filter size={20} />
              {hasActiveFilters && <span className="mobile-filter-badge"></span>}
            </button>
            <div className="filter-dropdowns d-none d-lg-flex">
              <Form.Select className="dark-filter-select" value={selectedAuditionType} onChange={(e) => setSelectedAuditionType(e.target.value)}>
                {auditionTypes.map((type) => (<option key={type} value={type}>{type.replace(/_/g, ' ')}</option>))}
              </Form.Select>
              <Form.Select className="dark-filter-select" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
                {cities.map((city) => (<option key={city} value={city}>{city}</option>))}
              </Form.Select>
              {talentTypes.length > 1 && (
                <Form.Select className="dark-filter-select" value={selectedTalentType} onChange={(e) => setSelectedTalentType(e.target.value)}>
                  {talentTypes.map((type) => (<option key={type} value={type}>{type}</option>))}
                </Form.Select>
              )}
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="clear-filters-btn" title="Clear Filters"><FilterX size={18} /></button>
              )}
            </div>
            <div className="browse-roles-search">
              <Search size={18} className="browse-search-icon" />
              <Form.Control
                type="text"
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="browse-search-input"
              />
              {searchTerm && (
                <button type="button" className="browse-clear-btn" onClick={() => setSearchTerm('')}><X size={16} /></button>
              )}
            </div>
          </div>

          {/* MAIN CONTENT AREA */}
          <div className="browse-roles-content w-100 mt-4">
            {loading ? (
              <div className="browse-empty-box"><Spinner animation="border" variant="danger" /></div>
            ) : filteredRoles.length > 0 ? (
              <Row className="g-3 w-100 m-0"> {/* Use g-3 for consistent gaps */}
                {filteredRoles.map((item, index) => (
                  // ✅ FIXED: Removed p-0 and used default Bootstrap column padding
                  <Col xxl={2} xl={3} lg={4} md={6} xs={6} key={item.id || item.localRoleId || index}>
                    <ModernAuditionCard audition={item} />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="browse-empty-box">
                <div className="browse-empty-icon"><Search size={56} strokeWidth={1.8} /></div>
                <h3>No roles found</h3>
                <p>Try adjusting your filters or search terms.</p>
                <button type="button" className="browse-clear-all" onClick={clearAllFilters}>Clear all filters</button>
              </div>
            )}
          </div>
            
        </Container>
      </section>

      {/* MODAL (remains the same) */}
      <Modal 
        show={showMobileFilters} 
        onHide={() => setShowMobileFilters(false)} 
        centered 
        dialogClassName="compact-dark-modal"
      >
        <Modal.Header closeButton closeVariant="white" className="modal-custom-header border-bottom border-dark">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2 text-white" style={{fontSize: '14px', letterSpacing: '1px'}}>
            <Filter size={18} className="text-danger" /> FILTER ROLES
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3 p-4">
          <div className="filter-group-popup">
            <label className="drawer-filter-label">AUDITION TYPE</label>
            <Form.Select className="popup-select w-100" value={selectedAuditionType} onChange={(e) => setSelectedAuditionType(e.target.value)}>
              {auditionTypes.map((type) => (<option key={type} value={type}>{type.replace(/_/g, ' ')}</option>))}
            </Form.Select>
          </div>
          <div className="filter-group-popup">
            <label className="drawer-filter-label">CITY</label>
            <Form.Select className="popup-select w-100" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
              {cities.map((city) => (<option key={city} value={city}>{city}</option>))}
            </Form.Select>
          </div>
          {talentTypes.length > 1 && (
            <div className="filter-group-popup">
              <label className="drawer-filter-label">TALENT TYPE</label>
              <Form.Select className="popup-select w-100" value={selectedTalentType} onChange={(e) => setSelectedTalentType(e.target.value)}>
                {talentTypes.map((type) => (<option key={type} value={type}>{type}</option>))}
              </Form.Select>
            </div>
          )}
          <div className="mt-3">
             <button className="btn-apply-filters w-100 mb-2" onClick={() => setShowMobileFilters(false)}>Apply Filters</button>
             {hasActiveFilters && (
                <button className="btn-clear-filters w-100" onClick={() => { clearAllFilters(); setShowMobileFilters(false); }}>Clear All</button>
             )}
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default BrowseRoles;